import { useMemo } from 'react';
import { Accordion } from 'components/Accordion';
import { QualificationBadges } from 'components/AgentCallConsole/components/QualificationBadges';
import { AgregatedQualificationResponse, QualificationsResponse } from 'services/api/AgentCenterAPI';
import { formatQualifications } from 'utils/livekitContext.utils';
import { groupAggregatedQualifications } from './groupAggregatedQualifications';
import styles from './AggregatedQualificationsAccordion.module.scss';

type AggregatedQualificationsAccordionProps = {
    qualifications?: AgregatedQualificationResponse[];
    isLoading?: boolean;
};

const formatSourceCallDate = (dateString: string) => {
    if (!dateString) {
        return '-';
    }

    return new Date(dateString).toLocaleString();
};

const toQualificationsResponse = (qualifications: AgregatedQualificationResponse[]): QualificationsResponse[] =>
    qualifications.map((qualification) => ({
        id: qualification.key,
        key: qualification.key,
        value: qualification.value,
        created_at: qualification.sourceCallDate,
    }));

export const AggregatedQualificationsAccordion = ({
    qualifications = [],
    isLoading = false,
}: AggregatedQualificationsAccordionProps) => {
    const groupedQualifications = useMemo(() => groupAggregatedQualifications(qualifications), [qualifications]);

    return (
        <div className={styles.container}>
            <h3 className={styles.title}>Qualifications</h3>

            {isLoading ? (
                <div className={styles.emptyState}>Loading qualification results...</div>
            ) : groupedQualifications.length ? (
                <div className={styles.accordionList}>
                    {groupedQualifications.map((group) => {
                        const formattedQualifications = formatQualifications(
                            toQualificationsResponse(group.qualifications)
                        );
                        const isGroupAllNoAnswer = group.qualifications.every(
                            (qualification) => qualification.value === 'NO_ANSWER'
                        );
                        const groupKey = group.sourceCallId || `no-source-${group.sourceCallDate}`;

                        return (
                            <Accordion
                                key={groupKey}
                                className={styles.accordion}
                                header={
                                    <div className={styles.accordionHeader}>
                                        <span className={styles.callId}>
                                            Call ID: {group.sourceCallId || 'Unknown call'}
                                        </span>
                                        <span className={styles.callDate}>
                                            {formatSourceCallDate(group.sourceCallDate)}
                                        </span>
                                    </div>
                                }
                            >
                                <QualificationBadges
                                    embedded
                                    showTitle={false}
                                    isAllNoAnswer={isGroupAllNoAnswer}
                                    qualifications={formattedQualifications}
                                />
                            </Accordion>
                        );
                    })}
                </div>
            ) : (
                <div className={styles.emptyState}>Sorry, no qualifications found</div>
            )}
        </div>
    );
};
